// frontend/src/components/proposals/SubstitutePanel.js
import React, { useEffect, useState } from 'react';
import { vendorPricesApi } from '../../utils/api';
import { fetchOffersForStems, cheapestByStem } from '../../utils/vendorOffers';

// Inline panel under a flower row: shows curated substitutes for `stemName`,
// each priced (cheapest live offer for `date`, or "no live price"). Choosing one
// calls onSwap(substituteName, offer|null). `reason` ('unavailable'|'over-min'|null)
// drives the header line.
export default function SubstitutePanel({ stemName, date, reason, onSwap, onClose }) {
  const [subs, setSubs] = useState(null); // null = loading, [] = none
  const [priced, setPriced] = useState(new Map());
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setBusy(true);
      let rows = [];
      try {
        rows = (await vendorPricesApi.substitutes(stemName))?.data || [];
      } catch {
        rows = [];
      }
      if (!active) return;
      setSubs(rows);
      if (rows.length) {
        try {
          const offers = await fetchOffersForStems({ stems: rows.map((r) => r.substitute_key), date });
          if (active) setPriced(cheapestByStem(offers));
        } catch {
          /* pricing is best-effort; names still swap + price manually */
        }
      }
      if (active) setBusy(false);
    })();
    return () => { active = false; };
  }, [stemName, date]);

  const header =
    reason === 'unavailable'
      ? `No live offer for "${stemName}" on this date — consider a substitute:`
      : reason === 'over-min'
      ? `"${stemName}" forces box waste — a substitute may order cleaner:`
      : `Substitutes for "${stemName}":`;

  return (
    <div className="rounded-md bg-warn-bg p-2 text-[11px] space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-warn-fg font-semibold">{header}</span>
        <button type="button" onClick={onClose} className="text-warn-fg hover:underline">close</button>
      </div>
      {subs === null || busy ? (
        <div className="text-warn-fg">Loading…</div>
      ) : subs.length === 0 ? (
        <div className="text-warn-fg">No curated alternatives — use "Price from vendors" to search manually.</div>
      ) : (
        <ul className="space-y-1">
          {subs.map((s) => {
            const offer = priced.get(s.substitute_key) || null;
            return (
              <li key={s.substitute_key} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onSwap(s.substitute_key, offer)}
                  className="rounded bg-floral-700 px-2 py-0.5 font-semibold text-white hover:bg-floral-800"
                >
                  Use
                </button>
                <span className="font-semibold text-warn-fg capitalize">{s.substitute_key}</span>
                <span className="tabular-nums text-warn-fg">
                  {offer ? `$${Number(offer.price_per_stem).toFixed(2)}/st` : 'no live price'}
                </span>
                {s.note ? <span className="text-warn-fg truncate">— {s.note}</span> : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
